"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Star } from "lucide-react";
function CreatorReviewConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(12)].map((_, i) => <div key={i} className="rounded-xl bg-muted/30 animate-pulse" data-index={i}>
            <div className="h-48 w-full bg-muted/50 rounded-t-xl" />
            <div className="p-6 space-y-4">
              <div className="h-6 bg-muted/50 rounded w-2/3 mx-auto" />
              <div className="flex gap-4 justify-center">
                <div className="h-12 bg-muted/50 rounded w-20" />
                <div className="h-12 bg-muted/50 rounded w-20" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-muted/50 rounded w-full" />
                <div className="h-4 bg-muted/50 rounded w-5/6" />
              </div>
              <div className="h-10 bg-muted/50 rounded-md" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-sm text-muted-foreground mb-4">
          Failed to load reviews. Please try again.
        </p>
        <Button onClick={refetch} variant="link" className="text-sm">
          Retry
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <p className="text-center text-muted-foreground py-12">
        No reviews available at the moment.
      </p>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => <Card key={post.id} className="overflow-hidden bg-[hsl(50,15%,98%)] pt-0 shadow-[inset_0_2px_8px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[inset_0_2px_8px_rgba(0,0,0,0.08),0_8px_24px_rgba(0,0,0,0.12)]" data-index={i}>
          <div className="overflow-hidden">
            {post.featuredImage && <img src={post.featuredImage} alt={post.title} className="h-48 w-full object-cover transition-transform duration-500 hover:scale-[1.02]" />}
          </div>

          <CardContent className="flex flex-col gap-4">
            <h3 className="text-xl font-semibold text-[var(--color-foreground)] line-clamp-2 text-center">
              {post.title}
            </h3>

            <div className="flex items-center justify-center gap-6">
              <div className="flex flex-col items-center gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">
                  Screenado Rating
                </span>
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, idx) => {
                const rating = Number(post.customFields?.screenado_rating) || 0;
                const isFilled = idx < rating;
                return <Star key={idx} className={`size-4 ${isFilled ? "fill-accent-dark text-accent-dark" : "text-muted-foreground fill-none"}`} />;
              })}
                </div>
              </div>
              <div className="flex flex-col items-center gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">
                  Parent Rating
                </span>
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, idx) => {
                const rating = Number(post.customFields?.parent_rating) || 0;
                const isFilled = idx < rating;
                return <Star key={idx} className={`size-4 ${isFilled ? "fill-secondary-dark text-secondary-dark" : "text-muted-foreground fill-none"}`} />;
              })}
                </div>
              </div>
            </div>

            {post.excerpt && <p className="text-sm text-muted-foreground line-clamp-3">
                {post.excerpt.replace(/<[^>]*>/g, '')}
              </p>}

            {post.link && <Link to={post.link} className="w-full">
                <Button className="w-full bg-[hsl(180,70%,55%)] text-[var(--color-foreground-accent)] hover:bg-[hsl(180,70%,60%)] active:bg-[hsl(180,70%,50%)]">
                  Read Review
                </Button>
              </Link>}
          </CardContent>
        </Card>)}
    </div>;
}
export default function CreatorReviewGrid() {
  return <section className="bg-background py-16 px-6 lg:px-8 !bg-[url(https://screenado.com/wp-content/uploads/2026/02/Screenado-Collage-Banner-4.jpg)]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-4">
            Review Library
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore all our in-depth reviews of kids' YouTube creators and channels
          </p>
        </div>

        <PostsProvider wp_query={{
        per_page: 12,
        order: "desc",
        orderby: "date",
        categories: [4]
      }}>
          <CreatorReviewConsumer />
        </PostsProvider>
      </div>
    </section>;
}